import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  User,
  Lock,
  Bell,
  ShieldCheck,
  Download,
  LogOut,
  Trash2,
  ChevronRight,
  Loader2,
  Brain,
  Sparkles,
} from "lucide-react";
import { AppShell } from "../components/AppShell";
import { useAuth } from "../hooks/useAuth";
import { api } from "../lib/api";

export function Settings() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [working, setWorking] = useState<null | "logout" | "export" | "delete">(
    null,
  );

  async function handleLogout() {
    setWorking("logout");
    await signOut();
    navigate("/", { replace: true });
  }

  async function handleExport() {
    setWorking("export");
    try {
      const data = await api.get<unknown>("/api/dsgvo/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `parble-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setWorking(null);
    }
  }

  async function handleDelete() {
    setWorking("delete");
    try {
      await api.post("/api/dsgvo/delete");
      navigate("/", { replace: true });
    } finally {
      setWorking(null);
    }
  }

  return (
    <AppShell>
      <section className="container max-w-2xl pt-6">
        <h1 className="text-2xl font-bold tracking-tight">Einstellungen</h1>
        <p className="mt-1 text-sm text-ink-500">
          {user?.email && (
            <>
              Angemeldet als <span className="font-semibold">{user.email}</span>
            </>
          )}
        </p>

        <div className="mt-8 space-y-8">
          <div className="relative overflow-hidden rounded-3xl bg-brand-gradient p-6 text-white shadow-glow">
            <div className="pointer-events-none absolute -top-10 -right-10 h-40 w-40 rounded-full bg-white/20 blur-3xl" />
            <div className="relative">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/20 px-3 py-1 text-xs font-semibold uppercase tracking-widest backdrop-blur-sm">
                <Sparkles className="h-3.5 w-3.5" />
                Bald verfügbar
              </span>
              <div className="mt-4 flex items-start gap-4">
                <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm">
                  <Brain className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="text-lg font-bold tracking-tight">
                    Ausführliche Persönlichkeitsanalyse
                  </h3>
                  <p className="mt-1 text-sm text-white/90">
                    20 Min, Parship-Niveau: Big Five, Beziehungsstile,
                    Deal-Breaker. Für präzisere Matches und einen sichtbaren
                    Badge in deinem Profil.
                  </p>
                  <button
                    type="button"
                    disabled
                    className="mt-4 inline-flex cursor-not-allowed items-center gap-2 rounded-full bg-white/30 px-4 py-2 text-sm font-semibold text-white/80 backdrop-blur-sm"
                  >
                    Vormerken
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-widest text-ink-500">
              Konto
            </h2>
            <ul className="card divide-y divide-border overflow-hidden">
              {[
                {
                  icon: User,
                  label: "Profil bearbeiten",
                  sub: "Name, Bio, Fotos — bald verfügbar",
                },
                {
                  icon: Lock,
                  label: "Passwort und MFA",
                  sub: "Sicherheit deines Accounts — bald verfügbar",
                },
                {
                  icon: Bell,
                  label: "Benachrichtigungen",
                  sub: "E-Mail bei neuen Matches — bald verfügbar",
                },
              ].map(({ icon: Icon, label, sub }) => (
                <li key={label}>
                  <div className="flex items-center gap-4 px-5 py-4 opacity-50">
                    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-surface text-ink-300">
                      <Icon className="h-5 w-5" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-ink-900">
                        {label}
                      </p>
                      <p className="text-xs text-ink-500">{sub}</p>
                    </div>
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-brand-500">
                      Bald
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-widest text-ink-500">
              Datenschutz
            </h2>
            <ul className="card divide-y divide-border overflow-hidden">
              <li>
                <button
                  type="button"
                  onClick={handleExport}
                  disabled={working !== null}
                  className="flex w-full items-center gap-4 px-5 py-4 text-left transition hover:bg-surface disabled:opacity-50"
                >
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-brand-50 text-brand-500">
                    {working === "export" ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <Download className="h-5 w-5" />
                    )}
                  </span>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-ink-900">
                      Daten exportieren
                    </p>
                    <p className="text-xs text-ink-500">
                      Alles, was wir über dich gespeichert haben (Art. 20 DSGVO)
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-ink-300" />
                </button>
              </li>
              <li>
                <div className="flex items-center gap-4 px-5 py-4 opacity-50">
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-surface text-ink-300">
                    <ShieldCheck className="h-5 w-5" />
                  </span>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-ink-900">
                      Blockierte Profile
                    </p>
                    <p className="text-xs text-ink-500">
                      Verwalten, wer dich nicht sehen kann — bald verfügbar
                    </p>
                  </div>
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-brand-500">
                    Bald
                  </span>
                </div>
              </li>
            </ul>
          </div>

          <div>
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-widest text-ink-500">
              Gefahrenzone
            </h2>
            <div className="card divide-y divide-border overflow-hidden">
              <button
                type="button"
                onClick={handleLogout}
                disabled={working !== null}
                className="flex w-full items-center gap-4 px-5 py-4 text-left transition hover:bg-surface disabled:opacity-50"
              >
                <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-surface text-ink-500">
                  {working === "logout" ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <LogOut className="h-5 w-5" />
                  )}
                </span>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-ink-900">Abmelden</p>
                  <p className="text-xs text-ink-500">
                    Du musst dich auf diesem Gerät erneut anmelden.
                  </p>
                </div>
                <ChevronRight className="h-4 w-4 text-ink-300" />
              </button>
              <button
                type="button"
                onClick={() =>
                  confirmDelete ? handleDelete() : setConfirmDelete(true)
                }
                disabled={working !== null}
                className="flex w-full items-center gap-4 px-5 py-4 text-left transition hover:bg-brand-50/50 disabled:opacity-50"
              >
                <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  {working === "delete" ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <Trash2 className="h-5 w-5" />
                  )}
                </span>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-brand-600">
                    {confirmDelete
                      ? "Wirklich löschen? Nochmal klicken zum Bestätigen"
                      : "Konto löschen"}
                  </p>
                  <p className="text-xs text-ink-500">
                    Unwiderruflich. Chats deiner Partner werden anonymisiert.
                  </p>
                </div>
                <ChevronRight className="h-4 w-4 text-ink-300" />
              </button>
            </div>
          </div>
        </div>
      </section>
    </AppShell>
  );
}
